import SiteFooter from "../layout/SiteFooter";

export default function HikariNarrative() {
    return (
        <div className="relative z-10 w-full min-h-screen bg-[#EAE8E4] text-[#2C2C2C] selection:bg-[#2C2C2C] selection:text-[#EAE8E4]">
            {/* 1. Hero / Intro */}
            <section className="relative w-full h-screen flex flex-col items-center justify-center p-6 overflow-hidden">
                {/* Full Bleed Portrait Placeholder */}
                <div className="absolute inset-0 z-0 bg-[#D8D5D0]">
                    {/* 
            TODO: Replace with actual image asset 
            Ideally a generated image based on: "Editorial fashion close-up portrait..."
          */}
                    <img
                        src="/media/Persona_Hero_Portrait.jpeg"
                        alt="Hikari Mana Portrait"
                        className="w-full h-full object-cover object-top opacity-90 grayscale-[20%] contrast-[1.05]"
                    />
                    <div className="absolute inset-0 bg-black/10 mix-blend-overlay" />
                </div>

                <div className="relative z-10 text-center text-white mix-blend-difference space-y-4 max-w-4xl mx-auto mt-[40vh]">
                    <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] text-center">
                        HIKARI — The Girl from the Equator
                    </h1>
                    <p className="font-serif italic text-xl md:text-2xl opacity-90 mt-4">
                        “I was born in the heat, but I was forged in the cold.”
                    </p>
                    <div className="w-px h-16 bg-white/50 mx-auto my-8" />
                    <p className="font-sans text-xs md:text-sm tracking-[0.2em] uppercase opacity-80 max-w-md mx-auto leading-relaxed">
                        Strategic Brand Partner &nbsp;•&nbsp; Creative Director &nbsp;•&nbsp; Narrative Architect
                    </p>
                </div>
            </section>

            <div className="max-w-screen-xl mx-auto px-6 md:px-12 pb-32">
                {/* 2. Narrative Biography */}
                <section className="py-24 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-start">
                    <div className="space-y-8">
                        <div className="w-full aspect-[4/5] overflow-hidden mb-8">
                            <img src="/media/Persona_Jakarta_Origins_Section.jpeg" alt="Jakarta Origins" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                        </div>
                        <span className="text-[10px] tracking-widest uppercase text-neutral-500">Genesis — Jakarta</span>
                        <p className="font-serif text-2xl md:text-3xl leading-snug text-neutral-800">
                            The air was always heavy—thick with jasmine, clove cigarettes, and the exhaust of a million motorbikes.
                        </p>
                        <p className="font-sans text-neutral-600 leading-relaxed max-w-md">
                            My childhood operated on “Rubber Time” (jam karet). Nothing was urgent; everything was negotiated over sweet tea. I learned the language of texture before I learned to read—running my hands over rough Batik copper stamps and feeling the weight of hand-dyed cotton. It was a world of sensory overload, where luxury meant time, not logos.
                        </p>
                        <p className="font-sans text-neutral-600 leading-relaxed max-w-md">
                            This is where I learned to code-switch. One moment, bargaining in the humid chaos of a street market; the next, sitting straight-backed at a colonial dinner table. Adaptability is my mother tongue.
                        </p>
                    </div>

                    <div className="md:mt-32 space-y-8">
                        <div className="w-full aspect-[4/5] overflow-hidden mb-8">
                            <img src="/media/Persona_Munich_Contrast_Section.jpeg" alt="Munich Contrast" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                        </div>
                        <span className="text-[10px] tracking-widest uppercase text-neutral-500">Thermal Shock — Munich</span>
                        <p className="font-serif text-2xl md:text-3xl leading-snug text-neutral-800">
                            At twelve, the humidity vanished. The air turned crisp, dry, and unforgiving.
                        </p>
                        <p className="font-sans text-neutral-600 leading-relaxed max-w-md">
                            Munich was a shock to the system. The chaos of the equator was replaced by Alpine order. Punctuality was no longer a suggestion; it was a religion. But in this cold, I discovered a new kind of warmth: the tactile sanctuary of heavy wool, cashmere, and shearling.
                        </p>
                        <p className="font-sans text-neutral-600 leading-relaxed max-w-md">
                            My aesthetic identity is defined by this “Thermal Tension”—the friction between tropical fluidity and structured European cold. I don't just dress for the weather; I dress for the contrast.
                        </p>
                    </div>
                </section>

                {/* 3. Philosophy — The Art of Dressing Slowly */}
                <section className="py-24 border-t border-neutral-300">
                    <div className="max-w-3xl mx-auto text-center space-y-6">
                        <div className="w-full h-64 md:h-96 overflow-hidden mb-12">
                            <img src="/media/Persona_Philosophy_ Uniform_Section.jpeg" alt="The Uniform" className="w-full h-full object-cover" />
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl text-neutral-900 mb-8">The Art of Dressing Slowly</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-left mt-12">
                            <div>
                                <h3 className="font-serif italic text-xl mb-4">The Uniform</h3>
                                <p className="font-sans text-neutral-600 leading-relaxed text-sm">
                                    A Uniform is not repetition; it is resolution. It is the act of curating selective essentials that age with you. The perfect trench, the weighted wool trouser, the silk shirt that feels like water.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-serif italic text-xl mb-4">Quiet Luxury</h3>
                                <p className="font-sans text-neutral-600 leading-relaxed text-sm">
                                    True luxury whispers. It is found in the provenance of the material, not the volume of the branding. It distinguishes "trend" (purchased to be seen) from "uniform" (purchased to be worn).
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 4. The Trinity of Self */}
                <section className="py-24">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Pillar 1 */}
                        <div className="p-8 bg-[#E6E4E0] rounded-sm">
                            <h3 className="font-display text-2xl mb-4">📍 The Sportive</h3>
                            <p className="font-serif italic text-neutral-500 mb-4 text-sm">Not a look — a life.</p>
                            <p className="font-sans text-neutral-600 text-sm leading-relaxed">
                                Tennis at sunrise on clay courts that smell of crushed brick. Disciplined movement along the Isar river, breath visible in the morning chill. The satisfaction of a technical fabric doing exactly what it was designed to do. Strength is the ultimate accessory.
                            </p>
                        </div>

                        {/* Pillar 2 */}
                        <div className="p-8 bg-[#E6E4E0] rounded-sm">
                            <h3 className="font-display text-2xl mb-4">📍 The Funny</h3>
                            <p className="font-serif italic text-neutral-500 mb-4 text-sm">Warmth over perfection.</p>
                            <p className="font-sans text-neutral-600 text-sm leading-relaxed">
                                Finding the Jakarta warmth in Munich softness. A dry wit that cuts through the pretension of a gallery opening. Believing that if you can't laugh at the absurdity of high fashion, you're taking it—and yourself—too seriously.
                            </p>
                        </div>

                        {/* Pillar 3 */}
                        <div className="p-8 bg-[#E6E4E0] rounded-sm">
                            <h3 className="font-display text-2xl mb-4">📍 The Seductive</h3>
                            <p className="font-serif italic text-neutral-500 mb-4 text-sm">Whispered confidence.</p>
                            <p className="font-sans text-neutral-600 text-sm leading-relaxed">
                                Late jazz nights in dim corners. The feeling of silk slipping against skin. A glance that says more than a monologue. Slow fashion is seductive because it takes its time; it demands to be touched, understood, and appreciated slowly.
                            </p>
                        </div>
                    </div>
                </section>

                {/* 6. Featured Work / Media Blocks */}
                <section className="py-24 space-y-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        <div className="aspect-[4/5] bg-neutral-300 relative overflow-hidden group hover:cursor-pointer">
                            <img src="/media/Persona_Thermal_Tension Series.jpeg" alt="Thermal Tension" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-neutral-400/20 group-hover:bg-transparent transition-colors duration-500" />
                        </div>
                        <div className="space-y-4">
                            <span className="text-[10px] tracking-widest uppercase text-neutral-500">Editorial</span>
                            <h3 className="font-display text-3xl md:text-4xl text-neutral-800">Portrait Series: Thermal Tension</h3>
                            <p className="font-sans text-neutral-600 leading-relaxed">
                                An exploration of contrast. Capturing the interplay between heavy alpine wool and bare skin, visualizing the "Thermal Shock" of my migration.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center md:flex-row-reverse">
                        <div className="aspect-square bg-neutral-300 relative overflow-hidden group hover:cursor-pointer md:order-2">
                            <img src="/media/Persona_Maximilianstrasse_Moments.jpeg" alt="Maximilianstraße Moments" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-neutral-400/20 group-hover:bg-transparent transition-colors duration-500" />
                        </div>
                        <div className="space-y-4 md:order-1 text-right md:text-right">
                            <span className="text-[10px] tracking-widest uppercase text-neutral-500">Street Style</span>
                            <h3 className="font-display text-3xl md:text-4xl text-neutral-800">Moments on Maximilianstraße</h3>
                            <p className="font-sans text-neutral-600 leading-relaxed">
                                documenting the quiet elegance of Munich’s most iconic avenue. Not the tourists, but the locals who have worn the same coat for twenty years.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-12">
                        <div className="space-y-4 cursor-pointer group">
                            <div className="aspect-[3/4] bg-neutral-300 relative overflow-hidden">
                                <img src="/media/Persona_Sportive_Lifestyle.jpeg" alt="Sportive Lifestyle" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                <div className="absolute bottom-4 left-4 bg-white/80 px-3 py-1 text-[10px] uppercase tracking-widest backdrop-blur-sm group-hover:bg-white transition-colors">Fitness</div>
                            </div>
                            <h4 className="font-serif italic text-lg">Sportive in the English Garden</h4>
                            <p className="text-xs text-neutral-500 font-sans">Running routes & recovery rituals.</p>
                        </div>
                        <div className="space-y-4 cursor-pointer group">
                            <div className="aspect-[3/4] bg-neutral-300 relative overflow-hidden">
                                <img src="/media/Persona_Jazz_Night_ Seductive.jpeg" alt="Jazz Night" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                <div className="absolute bottom-4 left-4 bg-white/80 px-3 py-1 text-[10px] uppercase tracking-widest backdrop-blur-sm group-hover:bg-white transition-colors">Lifestyle</div>
                            </div>
                            <h4 className="font-serif italic text-lg">Quiet Nights, Warm Souls</h4>
                            <p className="text-xs text-neutral-500 font-sans">Curating a home for slow living.</p>
                        </div>
                        <div className="space-y-4 cursor-pointer group">
                            <div className="aspect-[3/4] bg-neutral-300 relative overflow-hidden">
                                <img src="/media/Persona_Hero_Portrait_Alternative.jpeg" alt="Provenance & Craft" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                <div className="absolute bottom-4 left-4 bg-white/80 px-3 py-1 text-[10px] uppercase tracking-widest backdrop-blur-sm group-hover:bg-white transition-colors">Craft</div>
                            </div>
                            <h4 className="font-serif italic text-lg">Provenance & Craft</h4>
                            <p className="text-xs text-neutral-500 font-sans">Visiting the ateliers that refuse to speed up.</p>
                        </div>
                    </div>
                </section>


                {/* 5. Services & Collaboration Invitation */}
                <section className="pt-32 pb-12 md:pt-48 md:pb-24 text-center flex flex-col items-center">
                    <h2 className="font-display text-5xl md:text-7xl mb-8 text-neutral-900">Let’s Create Something Timeless.</h2>
                    <div className="w-px h-16 bg-neutral-400 mx-auto mb-12" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl text-left font-sans text-sm md:text-base leading-relaxed text-neutral-600 mb-16">
                        <div>
                            <h4 className="font-bold text-neutral-900 mb-4 uppercase tracking-widest text-xs">Offering</h4>
                            <ul className="space-y-2">
                                <li>— Creative Direction & Narrative Architecture</li>
                                <li>— Brand Identity Consulting for Heritage & Luxury</li>
                                <li>— Editorial Collaborations & Curation</li>
                                <li>— Styling for the "Uniform" Aesthetic</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-bold text-neutral-900 mb-4 uppercase tracking-widest text-xs">Declining</h4>
                            <ul className="space-y-2 opacity-70">
                                <li>— Trend Chasing & Micro-Trends</li>
                                <li>— Surface Aesthetics without depth</li>
                                <li>— Fast Fashion Partnerships</li>
                                <li>— Loud Luxury</li>
                            </ul>
                        </div>
                    </div>

                    <p className="font-serif italic text-xl text-neutral-500 max-w-2xl mb-12">
                        Inviting heritage brands, tenniswear partners, batik artisans, and wellness properties to define the next chapter of quiet luxury.
                    </p>

                    <a
                        href="mailto:hikari.mana@outlook.com"
                        className="inline-block px-12 py-4 bg-[#2C2C2C] text-[#EAE8E4] font-sans text-xs uppercase tracking-[0.2em] hover:bg-neutral-800 transition-colors duration-300"
                    >
                        Inquire
                    </a>
                </section>

                {/* 7. Footer */}
                <SiteFooter />
            </div>
        </div>
    );
}
